import axios from "axios";
import safeNoop from "./safeNoop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// =============================================================================
// Auth transport strategy
// =============================================================================
// Primary:   httpOnly cookies set by the backend on /auth/login + /auth/register
//            (carried automatically because axios is created with
//            withCredentials: true). This is preferred because the token is
//            unreachable from JavaScript and immune to XSS exfiltration.
//
// Fallback:  a short-lived Bearer token persisted in **sessionStorage**.
//            Required because cookies fail in several preview contexts:
//              - the deployed app embedded in cross-origin iframes
//              - Safari Intelligent Tracking Prevention dropping 3rd-party cookies
//              - strict cookie/CSP policies that block SameSite=lax
//            sessionStorage is per-tab and dies when the tab closes — that is a
//            deliberate hardening choice over localStorage. If both transports
//            fail, the user is simply unauthenticated; nothing leaks across
//            tabs or origins.
// =============================================================================

export const TOKEN_STORAGE_KEY = "dh_access_token";

/**
 * Pick the best available Storage. Prefer sessionStorage; if it's blocked
 * (Safari private browsing in some versions, strict CSP, quota), return null
 * and let the caller silently no-op. We never throw — losing the Bearer just
 * means we fall through to cookie-based auth.
 */
function pickStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    // Probe writability — sessionStorage exists but throws on write in some
    // hardened browser modes.
    const probe = "__dh_probe__";
    window.sessionStorage.setItem(probe, "1");
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch (e) {
    safeNoop("tokenStore.storage-unavailable", e);
    return null;
  }
}

export const tokenStore = {
  get() {
    const s = pickStorage();
    if (!s) return null;
    try { return s.getItem(TOKEN_STORAGE_KEY) || null; }
    catch (e) { safeNoop("tokenStore.get-failed", e); return null; }
  },
  set(token) {
    const s = pickStorage();
    if (!s) return; // cookie-only path; nothing to do
    try { s.setItem(TOKEN_STORAGE_KEY, token); }
    catch (e) { safeNoop("tokenStore.set-failed", e); }
  },
  clear() {
    const s = pickStorage();
    if (!s) return;
    try { s.removeItem(TOKEN_STORAGE_KEY); }
    catch (e) { safeNoop("tokenStore.clear-failed", e); }
  },
};

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,                 // send cookies if the browser allows
  headers: { "Content-Type": "application/json" },
});

// --- Request interceptor --------------------------------------------------
// Always attach the Bearer token (if we have one) on every request. The
// backend reads either the cookie OR the Authorization header — so this is
// purely additive and never breaks cookie-based callers.
api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// --- Response interceptor -------------------------------------------------
// On 401: clear local token, notify the AuthContext, and let the UI decide
// (typically redirect to /login). On other errors: normalize the message.
//
// We deliberately do NOT redirect from inside axios — that would race with
// React Router and rip the user out mid-render. We dispatch an event the
// AuthContext listens for, which then sets `user = false` and React Router
// handles the redirect cleanly.
const AUTH_FAILED_EVENT = "dh:auth-failed";

function safeDispatchAuthFailed(url) {
  // Wrapped because some hardened embed contexts may block CustomEvent
  // construction; missing the event simply leaves the user on the page —
  // their next request will 401 again and re-trigger this path.
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(AUTH_FAILED_EVENT, { detail: { url } }));
  } catch (e) {
    safeNoop("api.auth-event-blocked", e);
  }
}

api.interceptors.response.use(
  (r) => r,
  (e) => {
    const status = e?.response?.status;
    const url = e?.config?.url || "";

    // Surface a normalized message for UI consumption.
    const detail = e?.response?.data?.detail;
    let message = "Something went wrong.";
    if (typeof detail === "string") message = detail;
    else if (Array.isArray(detail))
      message = detail
        .map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d)))
        .join(" · ");
    else if (detail && typeof detail.msg === "string") message = detail.msg;
    else if (e.message) message = e.message;
    e.userMessage = message;

    // 401 on a protected route → drop the token and tell the app.
    // We exclude /auth/me itself: that endpoint is *meant* to 401 when the
    // user isn't logged in (e.g. on first app load), and treating it as a
    // session-expiry would create a noisy redirect loop.
    if (status === 401 && !url.endsWith("/auth/me") && !url.endsWith("/auth/login") && !url.endsWith("/auth/register")) {
      tokenStore.clear();
      safeDispatchAuthFailed(url);
      e.isAuthFailure = true;
    }

    return Promise.reject(e);
  }
);

// Helper for AuthContext to subscribe to forced logouts.
export function onAuthFailed(handler) {
  window.addEventListener(AUTH_FAILED_EVENT, handler);
  return () => window.removeEventListener(AUTH_FAILED_EVENT, handler);
}

export default api;
